/**
 * The registers' model as the screens get it, their settings, and what needs
 * each person: decisions waiting on them, actions they own coming due, work
 * to confirm.
 */
import type { ISODate } from "./dates";
import { actionRef, actionState, awaitingCheck, dueOf, type Action, type Milestone } from "./actions";
import { addDays } from "./dates";
import { decisionFlags, decisionRef, waitingOn, type ConsentGroup, type Decision, type DecisionType } from "./decisions";
import { nextReview, reviewState, riskRef, type LastIssue, type Risk, type RiskDomain, type RiskScale, type RiskTier } from "./risks";
import { obligationNow, obligationRef, type CompanyDates, type Obligation, type ObligationCategory } from "./obligations";
import { RELEASE_OPEN, releaseRef, releaseTiming, type Release, type ReleaseFramework, type SignOffReviewer } from "./releases";
import { isLive, monthOf, type MeasureDef, type MeasureSettings, type MeasureTarget, type MeasureValue } from "./measures";
import { canApproveSection, canWriteSection, entryFor, isCompiled, type Pack, type PackSection, type PackSettings } from "./packs";
import type { Meeting } from "./meetings";
import { defaultChair, minuted, minutesDue, type MinutesSummary } from "./minutes";
import type { Holiday } from "./calendar";
import type { Role, User } from "./types";

export interface RegisterSettings {
  /** Days ahead that count as "due soon", for actions and decision dates. */
  dueSoonDays: number;
  /** Closing an action needs a note or a link. */
  closeNeedsEvidence: boolean;
}
export const DEFAULT_REGISTER_SETTINGS: RegisterSettings = { dueSoonDays: 7, closeNeedsEvidence: true };

export interface RegistersModel {
  decisions: Decision[];
  actions: Action[];
  milestones: Milestone[];
  decisionTypes: DecisionType[];
  consentGroups: ConsentGroup[];
  risks: Risk[];
  riskScale: RiskScale;
  riskDomains: RiskDomain[];
  riskTiers: RiskTier[];
  /** The register as last issued: each risk's residual rating then, for movement. */
  lastIssue: LastIssue | null;
  obligations: Obligation[];
  obligationCategories: ObligationCategory[];
  company: CompanyDates;
  /** 1–12, from finance settings: where the financial year starts, for statutory dates. */
  yearStartMonth: number;
  releases: Release[];
  releaseFrameworks: ReleaseFramework[];
  reviewers: SignOffReviewer[];
  measures: MeasureDef[];
  measureValues: MeasureValue[];
  measureTargets: MeasureTarget[];
  measureSettings: MeasureSettings;
  packs: Pack[];
  packSections: PackSection[];
  packSettings: PackSettings;
  meetings: Meeting[];
  holidays: Holiday[];
  /** Each meeting's minutes, without their words. */
  minutes: MinutesSummary[];
  settings: RegisterSettings;
  people: Pick<User, "id" | "name" | "avatarUrl" | "role" | "active">[];
  /** Today in London, on the server: what overdue is counted from. */
  today: ISODate;
}

export type NeedKind = "decide" | "consent" | "review" | "final" | "overdue" | "due" | "check" | "risk_review" | "obligation" | "release" | "figure_enter" | "figure_confirm" | "pack_write" | "pack_approve" | "minutes_write" | "minutes_sign" | "minutes_circulate";
export const NEED_LABEL: Record<NeedKind, string> = {
  decide: "Waiting on you",
  consent: "Consent to record",
  review: "Review",
  final: "Final soon",
  overdue: "Overdue",
  due: "Due soon",
  check: "To confirm",
  risk_review: "Risk review",
  obligation: "Obligation",
  release: "Statutory change",
  figure_enter: "To enter",
  figure_confirm: "To confirm",
  pack_write: "To write",
  pack_approve: "To approve",
  minutes_write: "Minutes to write",
  minutes_sign: "Minutes to sign off",
  minutes_circulate: "Minutes to circulate",
};

export interface Need {
  key: string;
  kind: NeedKind;
  ref: string;
  title: string;
  /** The date it turns on, if any. */
  date: ISODate | null;
  tone: "danger" | "warn" | "info";
  decisionId?: string;
  leverId?: string | null;
  actionId?: string;
  riskId?: string;
  obligationId?: string;
  releaseId?: string;
  measureId?: string;
  packId?: string;
  /** Which pack page it's on, and its month. */
  packKind?: Pack["kind"];
  month?: string;
  sectionId?: string;
  meetingId?: string;
}

const TONE_RANK = { danger: 0, warn: 1, info: 2 } as const;
const KIND_RANK: Record<NeedKind, number> = { overdue: 0, obligation: 1, pack_write: 2, minutes_sign: 3, release: 4, decide: 5, pack_approve: 6, minutes_write: 7, minutes_circulate: 8, review: 9, risk_review: 10, final: 11, due: 12, figure_enter: 13, consent: 14, check: 15, figure_confirm: 16 };

/**
 * What needs this person, most pressing first. Directors also see consents to
 * record and decisions coming up for review or going final; nobody sees a
 * decision they're stepped aside from.
 */
export function needsYou(
  me: { id: string; role: Role },
  m: Pick<RegistersModel, "decisions" | "actions" | "milestones" | "settings" | "today"> & Partial<Pick<RegistersModel, "risks" | "riskDomains" | "riskTiers" | "obligations" | "company" | "yearStartMonth" | "releases" | "releaseFrameworks" | "measures" | "measureValues" | "packs" | "packSections" | "packSettings" | "meetings" | "holidays" | "minutes">>,
): Need[] {
  const out: Need[] = [];
  const director = me.role === "director";
  const soon = m.settings.dueSoonDays;
  for (const d of m.decisions) {
    if (d.approval?.recusedIds.includes(me.id)) continue;
    const base = { ref: decisionRef(d.ref), title: d.title, decisionId: d.id, leverId: d.leverId };
    const flags = decisionFlags(d, m.today, soon);
    if (waitingOn(d).includes(me.id)) out.push({ ...base, key: `decide:${d.id}`, kind: "decide", date: d.finalAfter, tone: flags.includes("final_soon") ? "danger" : "warn" });
    if (!director) continue;
    if (d.status === "awaiting_consent") out.push({ ...base, key: `consent:${d.id}`, kind: "consent", date: d.finalAfter, tone: "info" });
    if (flags.includes("review_due") || flags.includes("review_soon")) out.push({ ...base, key: `review:${d.id}`, kind: "review", date: d.reviewBy, tone: flags.includes("review_due") ? "warn" : "info" });
    if (flags.includes("final_soon") && d.status === "taken") out.push({ ...base, key: `final:${d.id}`, kind: "final", date: d.finalAfter, tone: "warn" });
  }
  for (const a of m.actions) {
    const base = { ref: actionRef(a.ref), title: a.title, actionId: a.id };
    if (a.ownerId === me.id && a.status === "open") {
      const due = dueOf(a, m.milestones);
      const state = actionState(a, due, m.today, soon);
      if (state === "overdue") out.push({ ...base, key: `overdue:${a.id}`, kind: "overdue", date: due, tone: "danger" });
      else if (state === "due_soon") out.push({ ...base, key: `due:${a.id}`, kind: "due", date: due, tone: "warn" });
    }
    if (a.createdBy === me.id && awaitingCheck(a)) out.push({ ...base, key: `check:${a.id}`, kind: "check", date: null, tone: "info" });
  }
  const soonDate = addDays(m.today, soon);
  for (const r of m.risks ?? []) {
    if (r.ownerId !== me.id || r.status !== "open") continue;
    const next = nextReview(r, m.riskTiers ?? []);
    const state = reviewState(next, m.today, soonDate);
    if (state !== "ok") out.push({ key: `risk:${r.id}`, kind: "risk_review", ref: riskRef(r, m.riskDomains?.find((d) => d.id === r.domainId)), title: r.statement, date: next, tone: state === "overdue" ? "warn" : "info", riskId: r.id });
  }
  for (const o of m.obligations ?? []) {
    if (o.ownerId !== me.id || !o.active || !m.company) continue;
    const now = obligationNow(o, { company: m.company, yearStartMonth: m.yearStartMonth ?? 7 }, m.today);
    if (now.state === "overdue" || now.state === "due_soon" || now.state === "to_confirm")
      out.push({ key: `obl:${o.id}`, kind: "obligation", ref: obligationRef(o.ref), title: o.title, date: now.due, tone: now.state === "overdue" ? "danger" : now.state === "due_soon" ? "warn" : "info", obligationId: o.id });
  }
  for (const r of m.releases ?? []) {
    if (r.ownerId !== me.id || !RELEASE_OPEN.includes(r.status)) continue;
    const t = releaseTiming(r, m.releaseFrameworks?.find((f) => f.id === r.frameworkId), m.today);
    // past its target to go live, or close to it
    const soonBy = t.deployBy && t.deployBy <= addDays(m.today, soon);
    if (t.overDeploy || t.overDetect || soonBy) out.push({ key: `rel:${r.id}`, kind: "release", ref: releaseRef(r.ref), title: r.what, date: t.deployBy, tone: t.overDeploy || t.overDetect ? "danger" : "warn", releaseId: r.id });
  }
  // last month's figures for the measures you own: to type in, or to confirm
  const lastMonth = monthOf(addDays(`${monthOf(m.today)}-01`, -1));
  const short = (month: string) => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(month.slice(5, 7)) - 1]!;
  for (const d of m.measures ?? []) {
    if (d.ownerId !== me.id || !d.active) continue;
    const values = (m.measureValues ?? []).filter((v) => v.measureId === d.id);
    const unconfirmed = values.filter((v) => !v.confirmedAt).map((v) => v.month).sort();
    const base = { title: d.name, date: null, tone: "info" as const, measureId: d.id };
    if (d.source === "entered" && isLive(d, lastMonth) && !values.some((v) => v.month === lastMonth)) out.push({ ...base, key: `figure_enter:${d.id}`, kind: "figure_enter", ref: short(lastMonth) });
    else if (unconfirmed.length) out.push({ ...base, key: `figure_confirm:${d.id}`, kind: "figure_confirm", ref: short(unconfirmed.at(-1)!) });
  }
  // the monthly pack: your sections to write, and sections waiting on a director
  for (const p of (m.packs ?? []).filter((x) => x.status === "open")) {
    for (const sec of (m.packSections ?? []).filter((x) => x.active && x.kind === p.kind && !isCompiled(x))) {
      const e = entryFor(p, sec.id);
      const base = { ref: short(p.month), title: sec.title, packId: p.id, packKind: p.kind, month: p.month, sectionId: sec.id };
      if (canWriteSection(me, sec) && (e.state === "todo" || e.state === "draft"))
        out.push({ ...base, key: `pack_write:${p.id}:${sec.id}`, kind: "pack_write", date: p.dueOn, tone: p.dueOn < m.today ? "danger" : p.dueOn <= addDays(m.today, soon) ? "warn" : "info" });
      if (e.state === "submitted" && m.packSettings && canApproveSection(me, sec, m.packSettings))
        out.push({ ...base, key: `pack_approve:${p.id}:${sec.id}`, kind: "pack_approve", date: p.meetingOn, tone: p.meetingOn <= addDays(m.today, soon) ? "warn" : "info" });
    }
  }
  // minutes: to draft, to sign off (never by the chair), to circulate
  if (director && m.meetings && m.packSettings) {
    const s = m.packSettings;
    const holidays = new Set((m.holidays ?? []).map((h) => h.date));
    const drafts = !s.minutesDrafterId || s.minutesDrafterId === me.id;
    for (const mt of m.meetings.filter((x) => minuted(x) && x.date <= m.today)) {
      const row = m.minutes?.find((x) => x.meetingId === mt.id);
      const state = row?.state ?? "awaiting";
      const due = minutesDue(mt.date, s.minutesDays, holidays);
      const base = { key: `minutes:${mt.id}`, ref: "Minutes", title: mt.title, date: due, meetingId: mt.id, tone: (due < m.today ? "danger" : "warn") as Need["tone"] };
      const chair = row ? row.chairId : defaultChair(mt, s);
      if ((state === "awaiting" || state === "draft") && drafts) out.push({ ...base, kind: "minutes_write" });
      if (state === "with_signer" && me.id !== chair && (row?.signerId ?? me.id) === me.id) out.push({ ...base, kind: "minutes_sign" });
      if (state === "signed" && drafts) out.push({ ...base, kind: "minutes_circulate" });
    }
  }
  return out.sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || KIND_RANK[a.kind] - KIND_RANK[b.kind] || (a.date ?? "9999").localeCompare(b.date ?? "9999") || a.ref.localeCompare(b.ref));
}
